import { NextRouter, useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { UrlObject } from 'url';
import { useOpenedPopupsValue, useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';

export const formatHref = (href: string | UrlObject): string => {
    if (typeof href === 'string') {
        return href;
    }

    const { pathname, query, hash } = href;
    const queryString = query ? new URLSearchParams(query as Record<string, string>).toString() : '';
    return `${pathname}${queryString ? `?${queryString}` : ''}${hash || ''}`;
};

export const normalizeOpenPopupParam = (value: string | null | undefined): string | null => {
    if (!value) {
        return null;
    }

    const normalized = value.replace(/\/+$/, '');
    return normalized || null;
};

export const getOpenPopupParamFromHref = (href: string): string | null => {
    try {
        const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
        const url = new URL(href, origin);
        return normalizeOpenPopupParam(new URLSearchParams(url.search).get('openPopup'));
    } catch (error) {
        console.error('Ошибка при разборе параметра openPopup:', error);
        return null;
    }
};

export const isSamePath = (href: string) => {
    try {
        const url = new URL(href, window.location.origin);
        const currentUrl = new URL(window.location.href);
        const currentPathname = currentUrl.pathname;

        return url.pathname === currentPathname;
    } catch (error) {
        console.error('Ошибка при проверке пути:', error);
        return false;
    }
};

type OpenPopupFunction = (popupName: string) => void;

export const handleOpenPopupFromHref = (href: string, openPopup: OpenPopupFunction) => {
    const popupName = getOpenPopupParamFromHref(href);

    if (popupName && typeof openPopup === 'function') {
        openPopup(popupName);
    }
};

export const clearOpenPopupFromUrl = (router: NextRouter) => {
    if (typeof window === 'undefined' || !getOpenPopupParamFromHref(window.location.href)) {
        return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('openPopup');

    const { openPopup: _, ...query } = router.query;
    const search = url.searchParams.toString();
    const newAsPath = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;

    router.replace({ pathname: router.pathname, query }, newAsPath, { shallow: true, scroll: false });
};

export const useOpenPopupFromGetParams = () => {
    const router = useRouter();
    const openedPopups = useOpenedPopupsValue();
    const prevOpenedPopupsRef = useRef<string[]>([]);
    const { openPopup } = useSetOpenedPopupsState();

    const openPopupFromUrl = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const popupName = getOpenPopupParamFromHref(window.location.href);

        if (popupName) {
            openPopup(popupName);
        }
    }, [openPopup]);

    useEffect(() => {
        if (!router.isReady) {
            return;
        }

        openPopupFromUrl();

        const onNewPageReady = () => {
            openPopupFromUrl();
        };

        document.addEventListener('new-page-ready', onNewPageReady);

        return () => {
            document.removeEventListener('new-page-ready', onNewPageReady);
        };
    }, [router.isReady, openPopupFromUrl]);

    useEffect(() => {
        if (!router.isReady) {
            return;
        }

        const popupFromUrl = getOpenPopupParamFromHref(window.location.href);

        if (!popupFromUrl) {
            prevOpenedPopupsRef.current = openedPopups;
            return;
        }

        const wasOpen = prevOpenedPopupsRef.current.includes(popupFromUrl);
        const isOpen = openedPopups.includes(popupFromUrl);

        if (wasOpen && !isOpen) {
            clearOpenPopupFromUrl(router);
        }

        prevOpenedPopupsRef.current = openedPopups;
    }, [openedPopups, router]);
};
